import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrowserSessionApiClient } from '@/services/browserSessionApiClient';
import { useAuth } from '@/components/Auth/AuthContext';
import { outlookQuotationOwnerIdentity } from '@/services/outlookQuotationDraftService';

const client = new BrowserSessionApiClient('/api');
const base = '/v1/mcp/workflows';
const uuid = /^[0-9a-f-]{36}$/i;
const kinds = ['task', 'quotation', 'invoice', 'attachment', 'client'];
type Args = Record<string, any>;

/** Fixed route bridge for the same UI asset used by the private MCP plugin.
 * SWA cookies stay in the parent API client. The iframe never receives credentials. */
export async function callWorkflowUiTool(name: string, args: Args) {
  const json = async (path: string, body?: unknown) => {
    const result = await client.request<{ data: any }>(path, body === undefined ? {} : { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
    return result.data;
  };
  const id = args.draftId;
  if (id !== undefined && (typeof id !== 'string' || !uuid.test(id))) throw new Error('Invalid workflow draft reference.');
  switch (name) {
    case 'pmapp_get_invoice_payments':
      if (!Number.isSafeInteger(args.invoiceId) || args.invoiceId < 1 || !Number.isSafeInteger(args.projectId) || args.projectId < 1) throw new Error('Choose a verified invoice and project.');
      return json(`${base}/invoices/${args.invoiceId}/payment-history?${new URLSearchParams({ projectId: String(args.projectId) })}`);
    case 'pmapp_search_workflow_clients': {
      if (!['client', 'quotation', 'invoice'].includes(args.kind) || typeof args.query !== 'string' || args.query.trim().length < 2 || args.query.trim().length > 120) throw new Error('Invalid client search.');
      return json(`${base}/clients/${args.kind}?${new URLSearchParams({ q: args.query.trim() })}`);
    }
    case 'pmapp_get_workflow_form':
      if (!kinds.includes(args.kind)) throw new Error('Invalid workflow type.');
      if (args.suburbQuery !== undefined && (args.kind !== 'quotation' || typeof args.suburbQuery !== 'string' || args.suburbQuery.trim().length < 2 || args.suburbQuery.trim().length > 100)) throw new Error('Invalid suburb lookup.');
      return json(`${base}/forms/${args.kind}${args.suburbQuery === undefined ? '' : '?' + new URLSearchParams({ suburbQuery: args.suburbQuery.trim() })}`);
    case 'pmapp_list_workflow_drafts': return json(`${base}/drafts`);
    case 'pmapp_get_workflow_draft': return json(`${base}/drafts/${id}`);
    case 'pmapp_save_workflow_draft': return args.revision
      ? json(`${base}/drafts/${id}/save`, { revision: args.revision, fields: args.fields })
      : json(`${base}/drafts`, { draftId: id, kind: args.kind, fields: args.fields });
    case 'pmapp_execute_workflow_draft': return json(`${base}/drafts/${id}/execute`, { revision: args.revision });
    case 'pmapp_preview_invoice_send': return json(`${base}/drafts/${id}/send-preview`);
    case 'pmapp_save_invoice_email': return json(`${base}/drafts/${id}/save-email`, { revision: args.revision, email: args.email });
    case 'pmapp_approve_invoice_send': return json(`${base}/drafts/${id}/approve-send`, { revision: args.revision, approval: args.approval });
    case 'pmapp_prepare_task_update':
      if (!Number.isSafeInteger(args.taskId) || args.taskId < 1 || !Number.isSafeInteger(args.projectId) || args.projectId < 1) throw new Error('Choose a verified task and project.');
      return json(`${base}/tasks/${args.taskId}/prepare-update`, { draftId: id, projectId: args.projectId, changes: args.changes });
    case 'pmapp_upload_workflow_file': return json(`${base}/drafts/${id}/upload`, { revision: args.revision, fileName: args.fileName, contentBase64: args.contentBase64 });
    case 'pmapp_reconcile_workflow_files': return json(`${base}/drafts/${id}/reconcile`, { revision: args.revision });
    case 'pmapp_check_workflow_upload': return json(`${base}/drafts/${id}/upload-status`, {});
    case 'pmapp_search_projects': {
      const query = new URLSearchParams({ q: String(args.query || '').slice(0, 200), page: '1', pageSize: '10' });
      return { data: await json(`/v1/projects?${query}`) };
    }
    default: throw new Error('This tool is not exposed by the PMapp workbench.');
  }
}
function WorkflowDraftFrame() {
  const frame = useRef<HTMLIFrameElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const identity = outlookQuotationOwnerIdentity(user);
  const [issue, setIssue] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const reply = (value: unknown) => { if (active) frame.current?.contentWindow?.postMessage(value, window.location.origin); };
    const receive = async (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.origin !== window.location.origin || event.data?.jsonrpc !== '2.0') return;
      const message = event.data;
      try {
        if (message.method === 'ui/initialize') {
          reply({ jsonrpc: '2.0', id: message.id, result: { protocolVersion: '2026-01-26', hostInfo: { name: 'PMapp', version: '1' }, hostCapabilities: { serverTools: {}, openLinks: {} }, hostContext: { theme: 'light' } } });
        } else if (message.method === 'ui/notifications/initialized') {
          const query = new URLSearchParams(location.search), id = query.get('draftId');
          if (id && !uuid.test(id)) throw new Error('Invalid draft reference.');
          const draft = id ? await callWorkflowUiTool('pmapp_get_workflow_draft', { draftId: id }) : null;
          if (query.get('continue') === 'quotation') {
            if (!draft || draft.kind !== 'quotation' || draft.state !== 'draft' || !identity) throw new Error('A current quotation intake draft is required.');
            if (!active) return;
            navigate('/quotations/workflow', { replace: true, state: {
              prefill: { ...draft.fields, successMessage: 'Loaded your saved PMapp quotation basics. Complete scope details and fees here before creating a quotation. No quotation has been created or sent.' },
              workflowQuotationDraft: { ownerIdentity: identity, draftId: draft.draftId },
            } });
            return;
          }
          const selected = draft?.kind || (kinds.includes(query.get('kind') || '') ? query.get('kind') : 'task');
          const form = await callWorkflowUiTool('pmapp_get_workflow_form', { kind: selected });
          reply({ jsonrpc: '2.0', method: 'ui/notifications/tool-result', params: { structuredContent: { form, draft } } });
        } else if (message.method === 'tools/call') {
          const value = await callWorkflowUiTool(String(message.params?.name || ''), message.params?.arguments || {});
          reply({ jsonrpc: '2.0', id: message.id, result: { structuredContent: value, content: [{ type: 'text', text: JSON.stringify(value) }] } });
        } else if (message.method === 'ui/open-link') {
          const target = new URL(String(message.params?.url || ''), window.location.origin);
          const original = /^\/api\/v1\/mcp\/workflows\/drafts\/[0-9a-f-]{36}\/file$/i.test(target.pathname)
            && Boolean(target.searchParams.get('fileId')) && (target.searchParams.get('fileId') || '').length <= 4000;
          if (target.origin !== 'https://aipm.linfinityeng.com' || target.username || target.password || target.pathname !== '/workflow-drafts' && !/^\/invoices\/[1-9]\d*$/.test(target.pathname) && !original) throw new Error('Unsupported workflow link.');
          window.open(`${target.pathname}${target.search}`, '_blank', 'noopener,noreferrer');
          reply({ jsonrpc: '2.0', id: message.id, result: {} });
        } else if (message.id !== undefined) {
          reply({ jsonrpc: '2.0', id: message.id, error: { code: -32601, message: 'Unsupported workbench method.' } });
        }
      } catch (error: any) {
        const text = 'The PMapp request could not complete. Reload the saved draft status before retrying.';
        if (message.id !== undefined) reply({ jsonrpc: '2.0', id: message.id, result: { isError: true, content: [{ type: 'text', text: JSON.stringify({ message: text }) }] } });
        else if (active) setIssue(text);
      }
    };
    window.addEventListener('message', receive);
    return () => { active = false; window.removeEventListener('message', receive); };
  }, [location.search, navigate, identity]);
  return <main style={{ padding: '6px', minWidth: 0 }}>
    {issue && <p role="alert">{issue}</p>}
    <iframe ref={frame} title="PMapp workflow drafts" src="/pmapp-workbench.html" style={{ display: 'block', width: '100%', height: 'calc(100dvh - 160px)', minHeight: '420px', border: '1px solid #dce5ef', borderRadius: '10px', background: '#fff' }} />
  </main>;
}
export default function WorkflowDraftsPage() {
  const { user } = useAuth();
  return <WorkflowDraftFrame key={outlookQuotationOwnerIdentity(user) || 'signed-out'} />;
}
